import { ChevronLeft, ChevronRight } from "lucide-react";

export const PAGE_SIZE = 8;

/**
 * Lightweight client-side pagination control.
 * Renders nothing if there's only one page.
 */
export default function Pagination({ page, total, pageSize = PAGE_SIZE, onChange }) {
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  if (totalPages <= 1) return null;

  const from = (page - 1) * pageSize + 1;
  const to = Math.min(page * pageSize, total);

  const go = (p) => onChange(Math.min(Math.max(1, p), totalPages));

  // Compact page list: always show first, last, current, and neighbors
  const pages = (() => {
    const set = new Set([1, totalPages, page, page - 1, page + 1]);
    return [...set].filter((p) => p >= 1 && p <= totalPages).sort((a, b) => a - b);
  })();

  return (
    <div
      data-testid="pagination"
      className="flex items-center justify-between gap-3 mt-4 text-xs font-mono"
    >
      <div className="text-ash-500">
        Mostrando <span className="text-ash-100">{from}–{to}</span> de{" "}
        <span className="text-ash-100">{total}</span>
      </div>
      <div className="flex items-center gap-1">
        <button
          data-testid="pagination-prev"
          disabled={page === 1}
          onClick={() => go(page - 1)}
          className="px-3 py-2 border border-ink-500 hover:border-brand text-ash-100 disabled:opacity-30 disabled:cursor-not-allowed inline-flex items-center gap-1 uppercase tracking-widest"
        >
          <ChevronLeft size={14} />
        </button>
        {pages.map((p, i) => {
          const prev = pages[i - 1];
          const gap = prev != null && p - prev > 1;
          return (
            <span key={p} className="flex items-center gap-1">
              {gap && <span className="text-ash-500 px-1">…</span>}
              <button
                data-testid={`pagination-page-${p}`}
                onClick={() => go(p)}
                className={`px-3 py-2 border min-w-[36px] uppercase tracking-widest ${
                  p === page
                    ? "bg-brand text-ink-800 border-brand font-semibold"
                    : "border-ink-500 text-ash-100 hover:border-brand"
                }`}
              >
                {p}
              </button>
            </span>
          );
        })}
        <button
          data-testid="pagination-next"
          disabled={page === totalPages}
          onClick={() => go(page + 1)}
          className="px-3 py-2 border border-ink-500 hover:border-brand text-ash-100 disabled:opacity-30 disabled:cursor-not-allowed inline-flex items-center gap-1 uppercase tracking-widest"
        >
          <ChevronRight size={14} />
        </button>
      </div>
    </div>
  );
}
